// Login — phone + password against the company app. Calls users/login with
// the RSA-encrypted password; on success the login tokens land in the auth
// slice and the user picks a company.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import { Button, Field, PasswordInput, PhoneInput } from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import AuthLayout from '@/layout/AuthLayout';
import { OTPType, useLoginMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import { setAuth } from '@/redux/auth/authSlice';
import { useAppDispatch } from '@/store/hooks';
import { validateAll, validateField, type FormErrors } from './validation';
import { setSecurity } from '@/redux/security/securitySlice';

const schema = yup.object({
  phone: yup
    .string()
    .required('error Phone number is required')
    .matches(/^\d{7,12}$/, { message: 'error_invalid_phone_number', excludeEmptyString: true }),
  password: yup.string().required('error_password_required'),
});

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [login, { isLoading: submitting }] = useLoginMutation();

  const [dialCode, setDialCode] = useState(defaultCountry.dial);
  const [values, setValues] = useState({ phone: '', password: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const setValue = (name: 'phone' | 'password', value: string) => {
    const next = { ...values, [name]: value };
    setValues(next);
    if (submitted) {
      validateField(schema, name, next).then((message) =>
        setErrors((prev) => ({ ...prev, [name]: message })),
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = await validateAll(schema, values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    try {
      const result = await login({
        countryCode: dialCode,
        mobileNumber: values.phone,
        password: values.password,
      }).unwrap();

      if (result.code === 'USER_PENDING' || result.code === 'PASSWORD_PENDING') {
        // Either the registration OTP was never verified, or no login password
        // is set — both require verifying the phone first. VerifyOtp routes on
        // to /set-password when the account has no password.
        navigate('/request-otp', {
          state: { otpType: OTPType.LOGIN, dialCode, phone: values.phone },
        });
        return;
      }

      dispatch(setAuth({ loginToken: result.token, loginRefreshToken: result.refreshToken }));
      dispatch(setSecurity({ serverPrivateKey: result.serverPrivateKey }));
      toast.success(t('toast_login_success'));
      // Check for pending invites first; /my-invites falls through to
      // /select-company when there are none.
      navigate('/my-invites');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='login'>
      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_welcome_to_midani')}</h1>
          <p className='auth-page__subtitle'>{t('text_login_to_company')}</p>
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <Field
            label={t('field_phone_number')}
            htmlFor='login-phone'
            error={errors.phone ? t(errors.phone) : undefined}
          >
            <PhoneInput
              id='login-phone'
              countries={countries}
              dialCode={dialCode}
              onDialCodeChange={setDialCode}
              value={values.phone}
              onChange={(digits) => setValue('phone', digits)}
              error={!!errors.phone}
            />
          </Field>

          <Field
            label={t('field_password')}
            htmlFor='login-password'
            error={errors.password ? t(errors.password) : undefined}
          >
            <PasswordInput
              id='login-password'
              placeholder={t('field_enter_password')}
              autoComplete='current-password'
              value={values.password}
              onChange={(e) => setValue('password', e.target.value)}
              error={!!errors.password}
            />
          </Field>

          <Button type='submit' size='lg' loading={submitting} className='auth-page__submit'>
            {t('button_login')}
          </Button>

          <p className='auth-page__alt'>
            {t('text_forgot_password')}{' '}
            <button
              type='button'
              className='auth-page__link'
              onClick={() => navigate('/forgot-password')}
            >
              {t('text_reset_password')}
            </button>
          </p>

          <Button variant='secondary' size='lg' onClick={() => navigate('/signup')}>
            {t('button_create_account')}
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
